<template>
  <div class="loading" v-if="fetchData">
    <load-data></load-data>
  </div>
  <div v-else class="faculty-hours">
    <div class="panel">
      <!-- all filters in one line: staff name search, department, then date range + Go -->
      <div class="filter-bar">
        <div class="filter-field search-field">
          <label>Staff</label>
          <input type="text" class="form-control form-control-sm" v-model="search" placeholder="Search by staff name" />
        </div>
        <div class="filter-field department-field">
          <label>Department</label>
          <select class="custom-select custom-select-sm" v-model="departmentId">
            <option :value="null">All Departments</option>
            <option v-for="department in departmentOptions" :key="department.id" :value="department.id">{{ department.name }}</option>
          </select>
        </div>

        <div class="filter-field ml-auto">
          <label>From</label>
          <input type="date" class="form-control form-control-sm" v-model="fromDate" :max="toDate || null" />
        </div>
        <div class="filter-field">
          <label>To</label>
          <input type="date" class="form-control form-control-sm" v-model="toDate" :min="fromDate || null" />
        </div>
        <button class="btn btn-primary btn-sm go-btn" @click="getFaculties(false)" :disabled="loadingHours">
          <b-spinner v-if="loadingHours" small></b-spinner>
          <span v-else>Go</span>
        </button>
      </div>

      <div class="p-3">
        <table class="table table-striped table-bordered mb-0">
          <thead class="thead-dark text-left">
            <th style="width:6%;white-space:nowrap">Sl No.</th>
            <th style="width:34%">Staff Name</th>
            <th style="width:40%">Department</th>
            <!-- hours are for the date range applied with Go or the whole academic year -->
            <th class="text-center" style="width:20%">
              Total Working Hours<span v-if="academicYearText"> for {{ academicYearText }}</span><span v-if="appliedFromDate || appliedToDate"> {{ appliedRangeText }}</span>
            </th>
          </thead>
          <!-- Go reloads the hours: filters stay, loader shows in place of the rows -->
          <tbody v-if="loadingHours">
            <tr>
              <td colspan="4" class="text-center py-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="text-muted mt-2">Loading...</div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-if="filteredFaculties.length === 0">
              <td colspan="4" class="text-center text-muted">No staff found</td>
            </tr>
            <tr v-for="(faculty, index) in filteredFaculties" :key="faculty.id">
              <td> {{ index+1 }} </td>
              <td> {{ faculty.name }} </td>
              <td>
                <span v-if="faculty.departmentNames.length">{{ faculty.departmentNames.join(', ') }}</span>
                <span v-else class="text-muted">Not assigned yet</span>
              </td>
              <td class="text-center"> {{ faculty.hours }} </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  async created() {
    await this.getFaculties();
  },

  data() {
    return {
      fetchData: false,
      faculties: [],
      search: "",
      departmentId: null,
      fromDate: "",
      toDate: "",
      loadingHours: false,
      appliedFromDate: "",
      appliedToDate: "",
      requestId: 0,
    };
  },

  computed: {
    // e.g. "2022-23 Even semester"
    academicYearText() {
      const [year, semester] = this.$root.default_academic_year || [];
      if (!year || year == '0') return '';
      if (!semester || semester == '0') return year;
      const name = String(semester);
      return `${year} ${name.charAt(0).toUpperCase()}${name.slice(1)} semester`;
    },
    // dates applied with the last Go, shown as dd-mm-yyyy
    appliedRangeText() {
      const format = (date) => date.split('-').reverse().join('-');
      if (this.appliedFromDate && this.appliedFromDate === this.appliedToDate) return `on ${format(this.appliedFromDate)}`;
      if (this.appliedFromDate && this.appliedToDate) return `from ${format(this.appliedFromDate)} to ${format(this.appliedToDate)}`;
      if (this.appliedFromDate) return `from ${format(this.appliedFromDate)}`;
      return `up to ${format(this.appliedToDate)}`;
    },
    // departments that have staff in the list, alphabetical
    departmentOptions() {
      const departments = {};
      this.faculties.forEach((f) => f.departmentList.forEach((d) => { departments[d.id] = d; }));
      return Object.values(departments).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    filteredFaculties() {
      const search = this.search.trim().toLowerCase();
      return this.faculties.filter((f) => {
        if (search && !f.name.toLowerCase().includes(search)) return false;
        if (this.departmentId) return f.departmentList.some((d) => d.id === this.departmentId);
        return true;
      });
    },
  },

  methods: {
    // active staff of the institute with their departments and attendance hours, alphabetical
    // first load shows the page loader, Go only shows the loader in the table; empty dates count all hours
    async getFaculties(firstLoad = true) {
      if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
        this.$toast.error("From date must be before To date", { position: "top", duration: 3000 });
        return;
      }

      if (firstLoad) this.fetchData = true;
      else this.loadingHours = true;

      // only the latest request updates the list, an older one finishing late is ignored
      const requestId = ++this.requestId;
      const fromDate = this.fromDate;
      const toDate = this.toDate;
      let url = this.$store.getters.getAPIKey.mainAPI + `/institutionusers/faculties?with_departments=1&with_attendance_hours=1`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      await axios.get(url)
        .then((response) => {
          if (requestId !== this.requestId) return;
          this.faculties = response.data
            .filter((f) => f.enroll_status === 'active')
            .map((f) => ({
              id: f.id,
              name: [f.first_name, f.last_name].filter(Boolean).join(' ').trim(),
              departmentList: (f.maingroups || []).map((d) => ({ id: d.id, name: d.name })),
              departmentNames: [...new Set((f.maingroups || []).map((d) => d.name))],
              hours: Number(f.attendance_hours) || 0,
            }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          this.appliedFromDate = fromDate;
          this.appliedToDate = toDate;
        })
        .catch(() => {
          if (requestId !== this.requestId) return;
          this.$toast.error("Failed to load staff list", { position: "top", duration: 3000 });
        });
      if (requestId !== this.requestId) return;
      this.fetchData = false;
      this.loadingHours = false;
    },
  }
};
</script>

<style scoped>
.faculty-hours {
  padding: 4px;
}

/* panel + filters */
.panel {
  background: #fff;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 14px 18px;
  background: #f9fafc;
  border-bottom: 1px solid #edf0f5;
}

.filter-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.filter-field .form-control,
.filter-field .custom-select {
  border-radius: 6px;
  border-color: #d9dee7;
}

.go-btn {
  min-width: 64px;
  height: 31px;
  border-radius: 6px;
  font-weight: 600;
}

.department-field {
  flex: 1 1 240px;
  max-width: 380px;
}

.search-field {
  flex: 0 1 240px;
}


</style>
